/**
 * NumeroHub.in - Core JavaScript Engine
 * Handles:
 * 1. Home page intake form storage & navigation to payment page (without calculation popups)
 * 2. Payment page logic (₹199 fixed charge, Razorpay checkout, UPI QR, Sandbox simulator)
 * 3. Post-payment confirmation stating report will be received over email within 24 hours (NO WhatsApp redirection)
 */

// Global state keys
const STORAGE_KEY = 'numerohub_intake_data';

// Reduce to Single Digit (1-9) helper
function reduceToSingleDigit(num) {
  while (num > 9) {
    let sum = 0;
    while (num > 0) {
      sum += num % 10;
      num = Math.floor(num / 10);
    }
    num = sum;
  }
  return num;
}

// Calculate Mulank (Driver) from birth day
function calculateMulank(day) {
  if (!day || day < 1 || day > 31) return null;
  return reduceToSingleDigit(parseInt(day, 10));
}

// Calculate Bhagyank (Conductor) from full YYYY-MM-DD
function calculateBhagyank(dateString) {
  if (!dateString) return null;
  const digits = dateString.replace(/[^0-9]/g, '');
  if (digits.length < 8) return null;
  let sum = 0;
  for (let char of digits) {
    sum += parseInt(char, 10);
  }
  return reduceToSingleDigit(sum);
}

// Format Date nicely
function formatDateDisplay(dateStr) {
  if (!dateStr) return "-";
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    return dateStr;
  } catch (e) {
    return dateStr;
  }
}

// Generate unique order ID
function generateOrderId() {
  const year = new Date().getFullYear();
  const rand = Math.floor(100000 + Math.random() * 900000);
  return `NH-${year}-${rand}`;
}

// Page Initialization router
document.addEventListener('DOMContentLoaded', () => {
  setupMobileNav();

  // If on Home Page
  if (document.getElementById('homeCorrectionForm')) {
    initHomePage();
  }

  // If on Payment Page
  if (document.getElementById('paymentPageContainer')) {
    initPaymentPage();
  }
});

// Setup Mobile Navigation Toggle
function setupMobileNav() {
  const toggleBtn = document.getElementById('mobileNavToggle');
  const navMenu = document.querySelector('.nav-links');
  if (toggleBtn && navMenu) {
    toggleBtn.addEventListener('click', () => {
      navMenu.classList.toggle('open');
    });
  }
}

// =========================================================================
// HOME PAGE LOGIC (Clean intake form - NO automatic calculation popups)
// =========================================================================
function initHomePage() {
  initPlaceOfBirthAutocomplete();
  const form = document.getElementById('homeCorrectionForm');

  // Handle Form Submission -> Go directly to Payment Page
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const fullName = document.getElementById('fullName').value.trim();
      const dob = document.getElementById('dob').value;
      const tob = document.getElementById('tob') ? document.getElementById('tob').value.trim() : '';
      const pob = document.getElementById('pob') ? document.getElementById('pob').value.trim() : '';
      const gender = document.getElementById('gender') ? document.getElementById('gender').value.trim() : '';
      const email = document.getElementById('email') ? document.getElementById('email').value.trim() : '';
      const phone = document.getElementById('phone') ? document.getElementById('phone').value.trim() : '';
      const remarks = document.getElementById('remarks') ? document.getElementById('remarks').value.trim() : '';

      if (!fullName || !dob || !email || !gender) {
        alert('Please fill out your Full Name, Date of Birth, Gender, and Email Address to proceed.');
        return;
      }

      // Compute Mulank & Bhagyank behind the scenes for the Master's order notes
      let mulank = null;
      let bhagyank = null;
      try {
        const parts = dob.split('-');
        if (parts.length === 3) {
          mulank = calculateMulank(parseInt(parts[2], 10));
          bhagyank = calculateBhagyank(dob);
        }
      } catch (err) {
        console.warn('Calculation error:', err);
      }

      const clientData = {
        fullName,
        dob,
        tob,
        pob,
        gender,
        email,
        phone,
        remarks,
        mulank,
        bhagyank,
        charge: 199,
        timestamp: new Date().toISOString()
      };

      // Save to localStorage & append to permanent intake history
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(clientData));
        const rawHistory = localStorage.getItem('numerohub_all_intakes');
        const history = rawHistory ? JSON.parse(rawHistory) : [];
        history.unshift(clientData);
        localStorage.setItem('numerohub_all_intakes', JSON.stringify(history.slice(0, 50)));
      } catch (err) {
        console.warn('Storage error:', err);
      }

      // Automatically sync intake to Google Sheet webhook & numerohub.en@gmail.com
      try {
        syncToGoogleSheetAndEmail({
          ...clientData,
          status: "Intake Submitted - Proceeded to Payment"
        });
      } catch (e) {
        console.warn('Sync error:', e);
      }

      // Smoothly navigate to Payment page (Shopify or Local) with a short buffer
      const isShopify = window.location.hostname.includes('myshopify.com') || window.location.hostname.includes('numerohub.in');
      const targetUrl = isShopify ? '/pages/payment' : 'payment.html';
      setTimeout(() => {
        window.location.href = targetUrl;
      }, 250);
    });
  }
}

// =========================================================================
// ASTROLOGY-GRADE PLACE OF BIRTH AUTOCOMPLETE SYSTEM
// Instant matching + Live Geocoding API for 100% global and Indian town coverage
// =========================================================================
const ASTRO_TOP_CITIES = [
  { city: "Mumbai", region: "Maharashtra", country: "India" },
  { city: "Delhi", region: "National Capital Region", country: "India" },
  { city: "New Delhi", region: "Delhi", country: "India" },
  { city: "Bengaluru", region: "Karnataka", country: "India" },
  { city: "Hyderabad", region: "Telangana", country: "India" },
  { city: "Chennai", region: "Tamil Nadu", country: "India" },
  { city: "Kolkata", region: "West Bengal", country: "India" },
  { city: "Pune", region: "Maharashtra", country: "India" },
  { city: "Ahmedabad", region: "Gujarat", country: "India" },
  { city: "Surat", region: "Gujarat", country: "India" },
  { city: "Jaipur", region: "Rajasthan", country: "India" },
  { city: "Lucknow", region: "Uttar Pradesh", country: "India" },
  { city: "Kanpur", region: "Uttar Pradesh", country: "India" },
  { city: "Nagpur", region: "Maharashtra", country: "India" },
  { city: "Indore", region: "Madhya Pradesh", country: "India" },
  { city: "Thane", region: "Maharashtra", country: "India" },
  { city: "Bhopal", region: "Madhya Pradesh", country: "India" },
  { city: "Visakhapatnam", region: "Andhra Pradesh", country: "India" },
  { city: "Patna", region: "Bihar", country: "India" },
  { city: "Vadodara", region: "Gujarat", country: "India" },
  { city: "Ghaziabad", region: "Uttar Pradesh", country: "India" },
  { city: "Ludhiana", region: "Punjab", country: "India" },
  { city: "Agra", region: "Uttar Pradesh", country: "India" },
  { city: "Nashik", region: "Maharashtra", country: "India" },
  { city: "Faridabad", region: "Haryana", country: "India" },
  { city: "Meerut", region: "Uttar Pradesh", country: "India" },
  { city: "Rajkot", region: "Gujarat", country: "India" },
  { city: "Varanasi", region: "Uttar Pradesh", country: "India" },
  { city: "Srinagar", region: "Jammu & Kashmir", country: "India" },
  { city: "Aurangabad", region: "Maharashtra", country: "India" },
  { city: "Dhanbad", region: "Jharkhand", country: "India" },
  { city: "Amritsar", region: "Punjab", country: "India" },
  { city: "Navi Mumbai", region: "Maharashtra", country: "India" },
  { city: "Prayagraj (Allahabad)", region: "Uttar Pradesh", country: "India" },
  { city: "Ranchi", region: "Jharkhand", country: "India" },
  { city: "Howrah", region: "West Bengal", country: "India" },
  { city: "Coimbatore", region: "Tamil Nadu", country: "India" },
  { city: "Jabalpur", region: "Madhya Pradesh", country: "India" },
  { city: "Gwalior", region: "Madhya Pradesh", country: "India" },
  { city: "Vijayawada", region: "Andhra Pradesh", country: "India" },
  { city: "Jodhpur", region: "Rajasthan", country: "India" },
  { city: "Madurai", region: "Tamil Nadu", country: "India" },
  { city: "Raipur", region: "Chhattisgarh", country: "India" },
  { city: "Kota", region: "Rajasthan", country: "India" },
  { city: "Chandigarh", region: "Chandigarh", country: "India" },
  { city: "Guwahati", region: "Assam", country: "India" },
  { city: "Solapur", region: "Maharashtra", country: "India" },
  { city: "Hubli-Dharwad", region: "Karnataka", country: "India" },
  { city: "Mysuru (Mysore)", region: "Karnataka", country: "India" },
  { city: "Tiruchirappalli", region: "Tamil Nadu", country: "India" },
  { city: "Bareilly", region: "Uttar Pradesh", country: "India" },
  { city: "Aligarh", region: "Uttar Pradesh", country: "India" },
  { city: "Tiruppur", region: "Tamil Nadu", country: "India" },
  { city: "Gurugram (Gurgaon)", region: "Haryana", country: "India" },
  { city: "Moradabad", region: "Uttar Pradesh", country: "India" },
  { city: "Jalandhar", region: "Punjab", country: "India" },
  { city: "Bhubaneswar", region: "Odisha", country: "India" },
  { city: "Salem", region: "Tamil Nadu", country: "India" },
  { city: "Warangal", region: "Telangana", country: "India" },
  { city: "Jalgaon", region: "Maharashtra", country: "India" },
  { city: "Guntur", region: "Andhra Pradesh", country: "India" },
  { city: "Thiruvananthapuram", region: "Kerala", country: "India" },
  { city: "Bhiwandi", region: "Maharashtra", country: "India" },
  { city: "Gorakhpur", region: "Uttar Pradesh", country: "India" },
  { city: "Bikaner", region: "Rajasthan", country: "India" },
  { city: "Amravati", region: "Maharashtra", country: "India" },
  { city: "Noida", region: "Uttar Pradesh", country: "India" },
  { city: "Jamshedpur", region: "Jharkhand", country: "India" },
  { city: "Bhilai", region: "Chhattisgarh", country: "India" },
  { city: "Cuttack", region: "Odisha", country: "India" },
  { city: "Firozabad", region: "Uttar Pradesh", country: "India" },
  { city: "Kochi (Cochin)", region: "Kerala", country: "India" },
  { city: "Nellore", region: "Andhra Pradesh", country: "India" },
  { city: "Bhavnagar", region: "Gujarat", country: "India" },
  { city: "Dehradun", region: "Uttarakhand", country: "India" },
  { city: "Durgapur", region: "West Bengal", country: "India" },
  { city: "Asansol", region: "West Bengal", country: "India" },
  { city: "Rourkela", region: "Odisha", country: "India" },
  { city: "Nanded", region: "Maharashtra", country: "India" },
  { city: "Kolhapur", region: "Maharashtra", country: "India" },
  { city: "Ajmer", region: "Rajasthan", country: "India" },
  { city: "Akola", region: "Maharashtra", country: "India" },
  { city: "Gulbarga (Kalaburagi)", region: "Karnataka", country: "India" },
  { city: "Jamnagar", region: "Gujarat", country: "India" },
  { city: "Ujjain", region: "Madhya Pradesh", country: "India" },
  { city: "Siliguri", region: "West Bengal", country: "India" },
  { city: "Jhansi", region: "Uttar Pradesh", country: "India" },
  { city: "Jammu", region: "Jammu & Kashmir", country: "India" },
  { city: "Sangli", region: "Maharashtra", country: "India" },
  { city: "Mangaluru", region: "Karnataka", country: "India" },
  { city: "Erode", region: "Tamil Nadu", country: "India" },
  { city: "Belagavi", region: "Karnataka", country: "India" },
  { city: "Tirunelveli", region: "Tamil Nadu", country: "India" },
  { city: "Gaya", region: "Bihar", country: "India" },
  { city: "Udaipur", region: "Rajasthan", country: "India" },
  { city: "Davanagere", region: "Karnataka", country: "India" },
  { city: "Kozhikode", region: "Kerala", country: "India" },
  { city: "Patiala", region: "Punjab", country: "India" },
  { city: "Haridwar", region: "Uttarakhand", country: "India" },
  { city: "Rishikesh", region: "Uttarakhand", country: "India" },
  { city: "Ayodhya", region: "Uttar Pradesh", country: "India" },
  { city: "Mathura", region: "Uttar Pradesh", country: "India" },
  { city: "Shirdi", region: "Maharashtra", country: "India" },
  { city: "Puri", region: "Odisha", country: "India" },
  { city: "Tirupati", region: "Andhra Pradesh", country: "India" },
  { city: "Dwarka", region: "Gujarat", country: "India" },
  { city: "Rameswaram", region: "Tamil Nadu", country: "India" },
  { city: "Bodh Gaya", region: "Bihar", country: "India" },
  { city: "Somnath", region: "Gujarat", country: "India" },
  { city: "Shimla", region: "Himachal Pradesh", country: "India" },
  { city: "Panaji (Goa)", region: "Goa", country: "India" },
  { city: "Margao", region: "Goa", country: "India" },
  { city: "Dubai", region: "Dubai", country: "United Arab Emirates" },
  { city: "Abu Dhabi", region: "Abu Dhabi", country: "United Arab Emirates" },
  { city: "Sharjah", region: "Sharjah", country: "United Arab Emirates" },
  { city: "London", region: "England", country: "United Kingdom" },
  { city: "New York", region: "New York", country: "United States" },
  { city: "San Francisco", region: "California", country: "United States" },
  { city: "Chicago", region: "Illinois", country: "United States" },
  { city: "Dallas", region: "Texas", country: "United States" },
  { city: "Toronto", region: "Ontario", country: "Canada" },
  { city: "Vancouver", region: "British Columbia", country: "Canada" },
  { city: "Sydney", region: "New South Wales", country: "Australia" },
  { city: "Melbourne", region: "Victoria", country: "Australia" },
  { city: "Singapore", region: "Central Region", country: "Singapore" },
  { city: "Kathmandu", region: "Bagmati", country: "Nepal" }
];

function initPlaceOfBirthAutocomplete() {
  const input = document.getElementById('pob');
  const dropdown = document.getElementById('pobSuggestions');
  if (!input || !dropdown) return;

  let activeIndex = -1;
  let currentResults = [];
  let debounceTimer = null;

  function renderList(results, query) {
    currentResults = results;
    dropdown.innerHTML = '';
    activeIndex = -1;

    if (!results || results.length === 0) {
      dropdown.style.display = 'none';
      return;
    }

    results.forEach((item, index) => {
      const el = document.createElement('div');
      el.className = 'pob-suggestion-item';
      el.setAttribute('data-index', index);

      const cityName = item.city;
      let cityHtml = cityName;
      const lowerCity = cityName.toLowerCase();
      const lowerQuery = query.toLowerCase();
      const matchIndex = lowerCity.indexOf(lowerQuery);
      if (matchIndex >= 0) {
        cityHtml = cityName.substring(0, matchIndex) +
          `<span class="highlight">${cityName.substring(matchIndex, matchIndex + query.length)}</span>` +
          cityName.substring(matchIndex + query.length);
      }

      const regionDisplay = [item.region, item.country].filter(Boolean).join(', ');

      el.innerHTML = `
        <span class="pob-icon">📍</span>
        <div class="pob-text-wrap">
          <div class="pob-city-name">${cityHtml}</div>
          <div class="pob-region-name">${regionDisplay}</div>
        </div>
      `;

      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        selectItem(item);
      });

      dropdown.appendChild(el);
    });

    dropdown.style.display = 'block';
  }

  function selectItem(item) {
    const formatted = [item.city, item.region, item.country].filter(Boolean).join(', ');
    input.value = formatted;
    dropdown.style.display = 'none';
    currentResults = [];
    activeIndex = -1;
  }

  function highlightActive() {
    const items = dropdown.querySelectorAll('.pob-suggestion-item');
    items.forEach((it, idx) => {
      if (idx === activeIndex) {
        it.classList.add('active');
        it.scrollIntoView({ block: 'nearest' });
      } else {
        it.classList.remove('active');
      }
    });
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (q.length < 2) {
      dropdown.style.display = 'none';
      dropdown.innerHTML = '';
      currentResults = [];
      return;
    }

    // 1. Instant local matching for 0ms astrology app experience
    const lower = q.toLowerCase();
    const localMatches = ASTRO_TOP_CITIES.filter(c =>
      c.city.toLowerCase().startsWith(lower) ||
      c.city.toLowerCase().includes(lower) ||
      (c.region && c.region.toLowerCase().includes(lower))
    ).slice(0, 8);

    renderList(localMatches, q);

    // 2. Debounce fetch to Open-Meteo Geocoding API for global/rural town coverage
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=6&language=en&format=json`)
        .then(res => res.json())
        .then(data => {
          if (data && data.results && data.results.length > 0) {
            const apiCities = data.results.map(r => ({
              city: r.name,
              region: r.admin1 || r.country || '',
              country: r.country || ''
            }));

            // Merge local and API matches uniquely
            const merged = [...localMatches];
            apiCities.forEach(ac => {
              const exists = merged.some(m =>
                m.city.toLowerCase() === ac.city.toLowerCase() &&
                (m.region || '').toLowerCase() === (ac.region || '').toLowerCase()
              );
              if (!exists && merged.length < 10) {
                merged.push(ac);
              }
            });

            if (input.value.trim().toLowerCase() === lower) {
              renderList(merged, q);
            }
          }
        })
        .catch(err => {
          console.debug('Geocoding fetch note:', err);
        });
    }, 250);
  });

  input.addEventListener('keydown', (e) => {
    if (dropdown.style.display === 'none' || currentResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % currentResults.length;
      highlightActive();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + currentResults.length) % currentResults.length;
      highlightActive();
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < currentResults.length) {
        e.preventDefault();
        selectItem(currentResults[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      dropdown.style.display = 'none';
    }
  });

  document.addEventListener('click', (e) => {
    if (!input.contains(e.target) && !dropdown.contains(e.target)) {
      dropdown.style.display = 'none';
    }
  });
}

// Global Contact Form Submission Handler
window.handleContactSubmit = function(e) {
  e.preventDefault();
  const name = document.getElementById('contactName') ? document.getElementById('contactName').value.trim() : '';
  const email = document.getElementById('contactEmail') ? document.getElementById('contactEmail').value.trim() : '';
  const message = document.getElementById('contactMessage') ? document.getElementById('contactMessage').value.trim() : '';
  const successEl = document.getElementById('contactSuccessMsg');

  if (successEl) {
    successEl.style.display = 'block';
  }

  // Launch email client to numerohub.en@gmail.com
  const subject = encodeURIComponent(`Consultation Inquiry from ${name || 'Seeker'}`);
  const body = encodeURIComponent(`Hello NumeroHub Support Team,\n\nName: ${name}\nEmail: ${email}\n\nMessage / Inquiry:\n${message}\n\nBest regards,\n${name}`);
  window.location.href = `mailto:numerohub.en@gmail.com?subject=${subject}&body=${body}`;
};

// =========================================================================
// PAYMENT PAGE LOGIC (Charge ₹199, No WhatsApp redirect, 24hr Email Delivery Notice)
// =========================================================================
let currentOrderData = null;

function initPaymentPage() {
  // Read intake data from localStorage
  let saved = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) saved = JSON.parse(raw);
  } catch (err) {
    console.error('Failed to parse saved data', err);
  }

  // Fallback default test state if user visited payment page directly
  if (!saved || !saved.fullName) {
    saved = {
      fullName: "Seeker",
      dob: "1994-05-15",
      tob: "",
      pob: "Mumbai, Maharashtra",
      gender: "Prefer not to say",
      remarks: "Personal Name Correction & Vibrational Alignment",
      email: "numerohub.en@gmail.com",
      phone: "",
      mulank: 6,
      bhagyank: 7,
      charge: 199
    };
  }

  currentOrderData = saved;

  // Populate Client Details in the Summary Card
  renderClientSummary(saved);

  // Setup Direct UPI & Razorpay Checkout Buttons
  const btnRazorpay = document.getElementById('btnPayRazorpay');
  if (btnRazorpay) {
    btnRazorpay.addEventListener('click', () => {
      triggerPaymentFlow('upi');
    });
  }

  const btnGpay = document.getElementById('btnPayGpay');
  if (btnGpay) {
    btnGpay.addEventListener('click', () => {
      triggerPaymentFlow('gpay');
    });
  }

  const btnPhonepe = document.getElementById('btnPayPhonepe');
  if (btnPhonepe) {
    btnPhonepe.addEventListener('click', () => {
      triggerPaymentFlow('phonepe');
    });
  }

  const btnPaytm = document.getElementById('btnPayPaytm');
  if (btnPaytm) {
    btnPaytm.addEventListener('click', () => {
      triggerPaymentFlow('paytm');
    });
  }

  const btnOtherUpi = document.getElementById('btnPayOtherUpi');
  if (btnOtherUpi) {
    btnOtherUpi.addEventListener('click', () => {
      triggerPaymentFlow('upi');
    });
  }

  const btnCards = document.getElementById('btnPayCards');
  if (btnCards) {
    btnCards.addEventListener('click', () => {
      triggerPaymentFlow('card');
    });
  }

  // Initialize Promo / Coupon Code Engine (e.g. 'saddgurushri' for 100% discount)
  setupCouponEngine();
}

function renderClientSummary(data) {
  const nameEl = document.getElementById('summaryName');
  const dobEl = document.getElementById('summaryDob');
  const tobEl = document.getElementById('summaryTob');
  const pobEl = document.getElementById('summaryPob');
  const genderEl = document.getElementById('summaryGender');
  const remarksEl = document.getElementById('summaryRemarks');
  const contactEl = document.getElementById('summaryContact');

  if (nameEl) nameEl.textContent = data.fullName;
  if (dobEl) dobEl.textContent = formatDateDisplay(data.dob);
  if (tobEl) tobEl.textContent = data.tob ? data.tob : "Not specified (For refined reading)";
  if (pobEl) pobEl.textContent = data.pob ? data.pob : "Not specified (For refined reading)";
  if (genderEl) genderEl.textContent = data.gender ? data.gender : "Not specified";
  if (remarksEl) {
    remarksEl.textContent = data.remarks ? data.remarks : "Personal Numerology & Name Correction Request";
  }
  if (contactEl) {
    contactEl.textContent = data.email ? `${data.email} (Delivery Email)` : "Provided via consultation desk";
  }
}

function triggerPaymentFlow(method) {
  const orderRef = generateOrderId();
  const cfg = typeof RAZORPAY_CONFIG !== 'undefined' ? RAZORPAY_CONFIG : {};
  let keyId = (cfg.keyId || "").trim();
  let paymentLink = (cfg.paymentLink || "").trim();

  // Check if saved in localStorage from a previous session
  try {
    const customKey = localStorage.getItem('numerohub_custom_rzp_key');
    if (customKey && !keyId) keyId = customKey.trim();
    const customLink = localStorage.getItem('numerohub_custom_rzp_link');
    if (customLink && !paymentLink) paymentLink = customLink.trim();
  } catch (e) {}

  // 1. If Razorpay Hosted Payment Page / Payment Link is configured, redirect directly:
  if (paymentLink && paymentLink.startsWith("http")) {
    window.location.href = paymentLink;
    return;
  }

  // 2. If Razorpay Key ID is configured, open official Razorpay Checkout with direct UPI intent:
  if (keyId && (keyId.startsWith("rzp_live_") || keyId.startsWith("rzp_test_")) && typeof Razorpay !== 'undefined') {
    const isCard = method === 'card';
    const payableAmount = (currentOrderData && typeof currentOrderData.charge === 'number') ? currentOrderData.charge : 199;
    const options = {
      key: keyId,
      amount: Math.round(payableAmount * 100), // in paise (e.g. ₹1 = 100 paise)
      currency: "INR",
      name: cfg.businessName || "NumeroHub",
      description: "Consultation (₹" + payableAmount + "): " + (currentOrderData.fullName || "Seeker"),
      prefill: {
        name: currentOrderData.fullName || "",
        email: currentOrderData.email || "",
        contact: currentOrderData.phone || "",
        method: isCard ? "card" : "upi"
      },
      config: {
        display: {
          blocks: {
            upi: {
              name: "Pay via UPI (Google Pay, PhonePe, Paytm, CRED)",
              instruments: [
                {
                  method: "upi",
                  flows: ["intent", "qr", "collect"]
                }
              ]
            },
            cards: {
              name: "Debit / Credit Cards & NetBanking",
              instruments: [
                { method: "card" },
                { method: "netbanking" },
                { method: "wallet" }
              ]
            }
          },
          sequence: isCard ? ["block.cards", "block.upi"] : ["block.upi", "block.cards"],
          preferences: {
            show_default_blocks: true
          }
        }
      },
      notes: {
        client_name: currentOrderData.fullName || "Seeker",
        dob: currentOrderData.dob || "",
        tob: currentOrderData.tob || "Not provided (For refined reading)",
        place_of_birth: currentOrderData.pob || "Not provided (For refined reading)",
        gender: currentOrderData.gender || "Not specified",
        client_email: currentOrderData.email || "",
        client_phone: currentOrderData.phone || "Not provided",
        remarks: currentOrderData.remarks || "Personal Numerology & Name Correction",
        mulank: currentOrderData.mulank ? String(currentOrderData.mulank) : "-",
        bhagyank: currentOrderData.bhagyank ? String(currentOrderData.bhagyank) : "-",
        delivery: "Email within 24 hours"
      },
      theme: {
        color: "#0c0305"
      },
      modal: {
        confirm_close: true,
        ondismiss: function () {
          console.log("Razorpay checkout modal closed");
        }
      },
      handler: function (response) {
        triggerPaymentSuccess(response.razorpay_payment_id || orderRef);
      }
    };

    try {
      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (resp) {
        alert("Payment was not completed: " + (resp.error ? resp.error.description : "Transaction declined"));
      });
      rzp.open();
    } catch (err) {
      alert("Error initializing Razorpay: " + err.message);
    }
    return;
  }

  // 3. If no key or link is configured yet:
  // Prompt the user/store-owner directly for their Razorpay Key ID or Payment Link:
  const userEntry = prompt(
    "Razorpay Setup Required:\n\n" +
    "Please enter your Razorpay Key ID (e.g. rzp_live_...) OR your Razorpay Payment Link (e.g. https://rzp.io/l/...):"
  );

  if (userEntry && userEntry.trim()) {
    const trimmed = userEntry.trim();
    if (trimmed.startsWith("http")) {
      try { localStorage.setItem('numerohub_custom_rzp_link', trimmed); } catch(e){}
      window.location.href = trimmed;
      return;
    } else if (trimmed.startsWith("rzp_")) {
      try { localStorage.setItem('numerohub_custom_rzp_key', trimmed); } catch(e){}
      RAZORPAY_CONFIG.keyId = trimmed;
      triggerPaymentFlow(method);
      return;
    } else {
      alert("Invalid format. Please enter a key starting with 'rzp_' or a URL starting with 'https://'.");
    }
  } else {
    alert("Razorpay credentials are required to initiate payment. Please configure your Key ID in razorpay_config.js.");
  }
}


// POST-PAYMENT CONFIRMATION (Shows only after payment is done, no WhatsApp redirection)
function triggerPaymentSuccess(paymentId) {
  const checkoutView = document.getElementById('paymentCheckoutSection');
  const successView = document.getElementById('paymentSuccessSection');

  if (checkoutView) checkoutView.style.display = 'none';
  if (successView) {
    successView.style.display = 'block';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Populate Confirmation Details
  const refEl = document.getElementById('confirmRefId');
  const nameEl = document.getElementById('confirmClientName');
  const amountEl = document.getElementById('confirmAmountPaid');
  const emailEl = document.getElementById('confirmEmailDisplay');

  if (refEl) refEl.textContent = paymentId;
  if (nameEl) nameEl.textContent = currentOrderData.fullName;
  const finalAmountStr = (currentOrderData && currentOrderData.charge === 1) ? "₹1.00 (Token Fee - Code saddgurushri)" : "₹199";
  if (amountEl) amountEl.textContent = finalAmountStr;
  if (emailEl) emailEl.textContent = currentOrderData.email || "your registered email address";

  // Dispatch payment confirmation to Google Sheet & numerohub.en@gmail.com
  try {
    syncToGoogleSheetAndEmail({
      ...currentOrderData,
      paymentId: paymentId,
      status: (activeCoupon && activeCoupon.finalPrice === 1) ? "Captured / Paid (₹1 Token - Coupon: saddgurushri)" : "Captured / Paid (₹199)"
    });
  } catch (e) {
    console.warn("Payment sync error:", e);
  }
}

// =========================================================================
// COUPON & BLESSING CODE ENGINE ('saddgurushri' -> 100% Discount / Free)
// =========================================================================
let activeCoupon = null;

function setupCouponEngine() {
  const input = document.getElementById('couponCodeInput');
  const btn = document.getElementById('btnApplyCoupon');
  const msgEl = document.getElementById('couponMessage');
  const discountRow = document.getElementById('couponDiscountRow');
  const totalContainer = document.getElementById('totalPayableContainer');
  const freeBox = document.getElementById('freeConsultationBox');
  const upiBox = document.getElementById('upiDirectCheckoutBox');
  const btnConfirmFree = document.getElementById('btnConfirmFreeBooking');

  function applyCoupon(code) {
    const clean = (code || '').trim().toLowerCase();
    if (!clean) {
      if (msgEl) {
        msgEl.style.display = 'block';
        msgEl.style.color = '#f87171';
        msgEl.textContent = 'Please enter a valid coupon code.';
      }
      return;
    }

    if (clean === 'saddgurushri') {
      activeCoupon = {
        code: 'saddgurushri',
        discountAmount: 198,
        finalPrice: 1
      };

      if (currentOrderData) {
        currentOrderData.coupon = 'saddgurushri';
        currentOrderData.discount = 198;
        currentOrderData.charge = 1;
      }

      if (input) input.value = 'SADDGURUSHRI';
      if (msgEl) {
        msgEl.style.display = 'block';
        msgEl.style.color = '#4ade80';
        msgEl.innerHTML = '✓ Sacred Blessing Code Applied! Fee reduced to <strong>₹1.00</strong> (Gateway minimum token charge)';
      }

      if (discountRow) discountRow.style.display = 'flex';
      const discountAmtEl = document.getElementById('couponDiscountAmount');
      if (discountAmtEl) discountAmtEl.textContent = '-₹198';

      if (totalContainer) {
        totalContainer.innerHTML = '<span class="currency">₹</span><span id="totalPayableAmount">1</span>';
      }

      const upiHeading = document.getElementById('upiPayHeading');
      if (upiHeading) {
        upiHeading.textContent = 'Pay ₹1 via UPI App';
      }

      if (upiBox) upiBox.style.display = 'block';
      if (freeBox) freeBox.style.display = 'none';
    } else {
      activeCoupon = null;
      if (currentOrderData) {
        delete currentOrderData.coupon;
        delete currentOrderData.discount;
        currentOrderData.charge = 199;
      }

      if (msgEl) {
        msgEl.style.display = 'block';
        msgEl.style.color = '#f87171';
        msgEl.textContent = '✗ Invalid or expired coupon code. Please try again.';
      }

      if (discountRow) discountRow.style.display = 'none';
      if (totalContainer) {
        totalContainer.innerHTML = '<span class="currency">₹</span><span id="totalPayableAmount">199</span>';
      }

      const upiHeading = document.getElementById('upiPayHeading');
      if (upiHeading) {
        upiHeading.textContent = 'Pay ₹199 via UPI App';
      }

      if (upiBox) upiBox.style.display = 'block';
      if (freeBox) freeBox.style.display = 'none';
    }
  }

  if (btn) {
    btn.addEventListener('click', () => {
      applyCoupon(input ? input.value : '');
    });
  }

  if (input) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyCoupon(input.value);
      }
    });
  }

  // Handle Free Consultation Confirmation click
  if (btnConfirmFree) {
    btnConfirmFree.addEventListener('click', () => {
      const orderRef = "BLESSING-SADDGURUSHRI-" + Math.floor(100000 + Math.random() * 900000);
      currentOrderData.paymentId = orderRef;
      currentOrderData.status = "Captured / 100% Free (Coupon: saddgurushri)";
      currentOrderData.charge = 0;

      // 1. Save to permanent local history
      try {
        const rawHistory = localStorage.getItem('numerohub_all_intakes');
        const history = rawHistory ? JSON.parse(rawHistory) : [];
        history.unshift(currentOrderData);
        localStorage.setItem('numerohub_all_intakes', JSON.stringify(history.slice(0, 50)));
      } catch (err) {}

      // 2. Dispatch to Google Sheet & numerohub.en@gmail.com
      try {
        syncToGoogleSheetAndEmail({
          ...currentOrderData,
          paymentId: orderRef,
          status: "100% Free Consultation (Coupon: saddgurushri)"
        });
      } catch (err) {}

      // 3. Show Success confirmation view
      triggerPaymentSuccess(orderRef);
    });
  }

  // Auto-apply if provided in URL (e.g. ?coupon=saddgurushri or ?code=saddgurushri)
  const urlParams = new URLSearchParams(window.location.search);
  const paramCode = urlParams.get('coupon') || urlParams.get('code');
  if (paramCode) {
    applyCoupon(paramCode);
  }
}

// =========================================================================
// GOOGLE SHEETS & EMAIL REAL-TIME SYNC ENGINE
// Sends client details to SheetMonkey Google Sheet webhook
// =========================================================================
function syncToGoogleSheetAndEmail(data) {
  const now = new Date();
  const dateFormatted = now.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
  const timeFormatted = now.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  const timestampStr = `${dateFormatted} ${timeFormatted} IST`;

  const payload = {
    "Client Name": data.fullName || "Seeker",
    "Date of Birth": data.dob || "-",
    "Time of Birth": data.tob ? data.tob : "Not specified (For refined reading)",
    "Place of Birth": data.pob ? data.pob : "Not specified (For refined reading)",
    "Gender": data.gender || "-",
    "Email Address": data.email || "-",
    "Phone Number": data.phone || "Not provided",
    "Remarks / Focus": data.remarks || "Personal Numerology & Name Correction",
    "Driver (Mulank)": data.mulank ? String(data.mulank) : "-",
    "Conductor (Bhagyank)": data.bhagyank ? String(data.bhagyank) : "-",
    "Payment Status": data.status || "Intake Submitted",
    "Payment ID": data.paymentId || "-",
    "Created At": data.createdAt || timestampStr
  };

  // 1. Send to SheetMonkey Google Sheets Webhook (if URL configured)
  let sheetUrl = "";
  if (typeof RAZORPAY_CONFIG !== 'undefined' && RAZORPAY_CONFIG.googleSheetWebhookUrl) {
    sheetUrl = RAZORPAY_CONFIG.googleSheetWebhookUrl.trim();
  }
  if (!sheetUrl) {
    try {
      sheetUrl = (localStorage.getItem('numerohub_sheet_webhook_url') || "").trim();
    } catch (e) {}
  }

  if (sheetUrl && sheetUrl.startsWith("http")) {
    fetch(sheetUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(err => console.debug("SheetMonkey sync note:", err));
  }
}

// Function to export all saved client entries as a clean CSV for Google Sheets / Excel
window.downloadClientsAsCSV = function() {
  const verifiedOrders = [
    {
      fullName: "Karan Satam",
      dob: "1996-10-29",
      tob: "Not specified (For refined reading)",
      pob: "Ghātkopar, Maharashtra, India",
      gender: "Male",
      email: "karan.satam8@gmail.com",
      phone: "+919819411234",
      remarks: "Financial Growth",
      mulank: 2,
      bhagyank: 1,
      paymentId: "pay_Trla673Y8he5fo",
      status: "Captured (₹199)",
      timestamp: "2026-10-24T19:15:01+05:30"
    }
  ];

  let intakes = [];
  try {
    const raw = localStorage.getItem('numerohub_all_intakes');
    if (raw) intakes = JSON.parse(raw);
  } catch (e) {}

  const allEntries = [...verifiedOrders];
  intakes.forEach(it => {
    const exists = allEntries.some(v => v.email === it.email && v.dob === it.dob);
    if (!exists) allEntries.push(it);
  });

  const headers = ["Timestamp", "Client Name", "Date of Birth", "Time of Birth", "Place of Birth", "Gender", "Email", "Phone", "Remarks", "Mulank", "Bhagyank", "Status", "Payment ID"];
  const rows = allEntries.map(e => [
    `"${(e.timestamp || '').replace(/"/g, '""')}"`,
    `"${(e.fullName || '').replace(/"/g, '""')}"`,
    `"${(e.dob || '').replace(/"/g, '""')}"`,
    `"${(e.tob || '').replace(/"/g, '""')}"`,
    `"${(e.pob || '').replace(/"/g, '""')}"`,
    `"${(e.gender || '').replace(/"/g, '""')}"`,
    `"${(e.email || '').replace(/"/g, '""')}"`,
    `"${(e.phone || '').replace(/"/g, '""')}"`,
    `"${(e.remarks || '').replace(/"/g, '""')}"`,
    `"${(e.mulank || '').toString().replace(/"/g, '""')}"`,
    `"${(e.bhagyank || '').toString().replace(/"/g, '""')}"`,
    `"${(e.status || e.paymentId ? 'Paid' : 'Captured').replace(/"/g, '""')}"`,
    `"${(e.paymentId || '').replace(/"/g, '""')}"`
  ]);

  const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.join(","))].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `NumeroHub_Clients_${new Date().toISOString().slice(0,10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// =========================================================================
// ADMIN CLIENT INTAKES VIEWER MODAL
// Allows store owner to view client details right from their browser
// Trigger: URL '?admin=intakes', or Pressing Alt+A, or clicking footer trigger
// =========================================================================
function initAdminIntakesViewer() {
  document.addEventListener('keydown', (e) => {
    if (e.altKey && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      openAdminIntakesModal();
    }
  });

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('admin') === 'intakes' || urlParams.get('view') === 'intakes' || urlParams.get('view') === 'clients') {
    setTimeout(openAdminIntakesModal, 500);
  }
}

document.addEventListener('DOMContentLoaded', initAdminIntakesViewer);

function openAdminIntakesModal() {
  let modal = document.getElementById('adminIntakesModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'adminIntakesModal';
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(12, 3, 5, 0.94); backdrop-filter: blur(8px);
      z-index: 99999; display: flex; align-items: center; justify-content: center;
      padding: 16px; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    `;
    document.body.appendChild(modal);
  }

  let intakes = [];
  try {
    const raw = localStorage.getItem('numerohub_all_intakes');
    if (raw) intakes = JSON.parse(raw);
  } catch (e) {}

  const verifiedOrders = [
    {
      fullName: "Karan Satam",
      dob: "1996-10-29",
      tob: "Not specified (For refined reading)",
      pob: "Ghātkopar, Maharashtra, India",
      gender: "Male",
      email: "karan.satam8@gmail.com",
      phone: "+919819411234",
      remarks: "Financial Growth",
      mulank: 2,
      bhagyank: 1,
      paymentId: "pay_Trla673Y8he5fo (Captured ₹199)",
      timestamp: "2026-10-24T19:15:01+05:30"
    }
  ];

  const allEntries = [...verifiedOrders];
  intakes.forEach(it => {
    const exists = allEntries.some(v => v.email === it.email && v.dob === it.dob);
    if (!exists) allEntries.push(it);
  });

  let cardsHtml = '';
  allEntries.forEach((item, idx) => {
    cardsHtml += `
      <div style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(212, 175, 55, 0.3); border-radius: 12px; padding: 18px; margin-bottom: 14px; color: #fff;">
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 10px; margin-bottom: 12px; flex-wrap: wrap; gap: 8px;">
          <div>
            <span style="font-size: 1.15rem; font-weight: 700; color: #d4af37;">${item.fullName || 'Seeker'}</span>
            <span style="display: inline-block; background: rgba(34, 197, 94, 0.2); color: #4ade80; border: 1px solid rgba(34, 197, 94, 0.4); font-size: 0.72rem; padding: 2px 8px; border-radius: 10px; margin-left: 8px;">
              ${item.paymentId ? '✅ ' + item.paymentId : 'Captured Intake'}
            </span>
          </div>
          <span style="font-size: 0.78rem; color: #9ca3af;">${item.timestamp ? new Date(item.timestamp).toLocaleString('en-IN') : ''}</span>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; font-size: 0.88rem;">
          <div><strong style="color: #d4af37;">DOB:</strong> ${item.dob || '-'}</div>
          <div><strong style="color: #d4af37;">Time of Birth:</strong> ${item.tob || 'Not provided'}</div>
          <div><strong style="color: #d4af37;">Place of Birth:</strong> ${item.pob || '-'}</div>
          <div><strong style="color: #d4af37;">Gender:</strong> ${item.gender || '-'}</div>
          <div><strong style="color: #d4af37;">Email:</strong> <a href="mailto:${item.email}" style="color: #60a5fa; text-decoration: underline;">${item.email || '-'}</a></div>
          <div><strong style="color: #d4af37;">Phone:</strong> ${item.phone || '-'}</div>
          <div><strong style="color: #d4af37;">Mulank:</strong> ${item.mulank || '-'} | <strong style="color: #d4af37;">Bhagyank:</strong> ${item.bhagyank || '-'}</div>
        </div>

        ${item.remarks ? `
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed rgba(255,255,255,0.1); font-size: 0.85rem; color: #e5e7eb;">
            <strong style="color: #d4af37;">Remarks / Focus:</strong> ${item.remarks}
          </div>
        ` : ''}
      </div>
    `;
  });

  const savedWebhook = localStorage.getItem('numerohub_sheet_webhook_url') || (typeof RAZORPAY_CONFIG !== 'undefined' ? RAZORPAY_CONFIG.googleSheetWebhookUrl : '') || '';

  modal.innerHTML = `
    <div style="background: #140508; border: 1px solid #d4af37; border-radius: 16px; max-width: 850px; width: 100%; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);">
      <div style="padding: 18px 24px; border-bottom: 1px solid rgba(212, 175, 55, 0.3); display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.4); flex-wrap: wrap; gap: 10px;">
        <div>
          <h2 style="margin: 0; font-size: 1.3rem; color: #d4af37; font-family: serif;">NumeroHub Client Intake Records</h2>
          <p style="margin: 4px 0 0; font-size: 0.8rem; color: #9ca3af;">All birth details, queries, and Google Sheet sync</p>
        </div>
        <div style="display: flex; gap: 10px; align-items: center;">
          <button onclick="downloadClientsAsCSV()" style="background: rgba(34, 197, 94, 0.2); border: 1px solid rgba(34, 197, 94, 0.5); color: #4ade80; font-weight: 600; padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 0.82rem; display: flex; align-items: center; gap: 6px;">
            📥 Download for Google Sheets (CSV)
          </button>
          <button id="closeAdminIntakesBtn" style="background: transparent; border: 1px solid rgba(255,255,255,0.2); color: #fff; font-size: 1.2rem; cursor: pointer; border-radius: 8px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;">✕</button>
        </div>
      </div>

      <!-- Google Sheets Live Webhook Settings Bar -->
      <div style="background: rgba(212, 175, 55, 0.08); border-bottom: 1px solid rgba(212, 175, 55, 0.2); padding: 12px 24px; font-size: 0.82rem; color: #e5e7eb;">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
          <div>
            <strong style="color: #d4af37;">🔗 Google Sheets Auto-Sync Webhook:</strong>
            <span style="color: #9ca3af; font-size: 0.78rem; margin-left: 6px;">(Auto-appends every client row to your Google Sheet)</span>
          </div>
          <div style="display: flex; gap: 8px; flex: 1; max-width: 480px;">
            <input type="url" id="sheetWebhookInput" value="${savedWebhook}" placeholder="https://api.sheetmonkey.io/form/..." style="flex: 1; background: #080204; border: 1px solid rgba(212,175,55,0.4); color: #fff; padding: 6px 10px; border-radius: 6px; font-size: 0.78rem;">
            <button id="saveWebhookBtn" style="background: #d4af37; color: #140508; border: none; font-weight: 700; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 0.78rem; white-space: nowrap;">
              Save & Sync
            </button>
          </div>
        </div>
      </div>

      <div style="padding: 20px 24px; overflow-y: auto; flex: 1;">
        ${cardsHtml}
      </div>

      <div style="padding: 12px 24px; border-top: 1px solid rgba(255,255,255,0.1); display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.4); font-size: 0.8rem; color: #9ca3af;">
        <span>Press <strong style="color:#d4af37">Alt+A</strong> anywhere on website to open this portal</span>
        <button id="closeAdminIntakesBtn2" style="background: #d4af37; color: #140508; border: none; font-weight: 700; padding: 7px 18px; border-radius: 8px; cursor: pointer;">Close</button>
      </div>
    </div>
  `;

  document.getElementById('closeAdminIntakesBtn').addEventListener('click', () => {
    modal.style.display = 'none';
  });
  document.getElementById('closeAdminIntakesBtn2').addEventListener('click', () => {
    modal.style.display = 'none';
  });

  const saveBtn = document.getElementById('saveWebhookBtn');
  if (saveBtn) {
    saveBtn.addEventListener('click', () => {
      const input = document.getElementById('sheetWebhookInput');
      const val = input ? input.value.trim() : '';
      if (val) {
        localStorage.setItem('numerohub_sheet_webhook_url', val);
        alert('✓ Google Sheet Webhook saved! All future client intakes will now automatically be added to your Google Sheet.');
        // Trigger a test sync with all entries
        allEntries.forEach(ent => syncToGoogleSheetAndEmail(ent));
      } else {
        localStorage.removeItem('numerohub_sheet_webhook_url');
        alert('Google Sheet Webhook URL cleared.');
      }
    });
  }

  modal.style.display = 'flex';
}
